import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { WebPurchaseRedemptionResultType } from "@revenuecat/purchases-capacitor";
import type { WebPurchaseRedemptionResult } from "@revenuecat/purchases-capacitor";
import {
  isEmulator,
  logException,
  logUserProperty,
  useAuthStore,
} from "@um/ui";
import {
  usePaymentsStore,
  type SubscriptionLifecycleStatus,
  type SubscriptionSnapshot,
} from "@/store/usePaymentsStore";
import {
  loadRevenueCatPlatform,
  peekRevenueCatPlatform,
  ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT,
  ROUTINE_PAYWALL_CLICK_PLACEMENT,
  type EntitlementIdentifier,
  type PaywallPlacementIdentifier,
  type PaywallPresentationRequest,
} from "./revenueCat";
import {
  isRevenueCatWebRedemptionUrl,
  redeemWebPurchaseFromUrl,
  REVENUECAT_WEB_REDEMPTION_URL_SCHEME,
} from "./revenueCat/platformNative";

export type { SubscriptionLifecycleStatus, SubscriptionSnapshot };
export type { EntitlementIdentifier, PaywallPlacementIdentifier };
export {
  ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT,
  ROUTINE_PAYWALL_CLICK_PLACEMENT,
  ROUTINE_PAYWALL_CLICK_ONBOARDING_STEP_ID,
} from "./revenueCat";

/**
 * RevenueCat paywall / customer center must not be presented concurrently: backgrounding
 * and resuming can re-trigger callers while a native sheet is still up. Reuse one in-flight
 * promise so overlapping calls await the same presentation instead of stacking modals.
 */
let revenueCatUiInFlight: Promise<unknown> | null = null;

let pendingWebPurchaseRedemptionUrl: string | null = null;
let webPurchaseRedemptionSessionSeq = 0;

/**
 * In-memory session cache of active entitlement IDs to avoid making redundant
 * network requests to RevenueCat's `/subscribers/<userId>` endpoint on every click.
 */
let cachedEntitlements: string[] | null = null;

export function isWebPurchaseRedemptionLink(urlString: string): boolean {
  return (
    Capacitor.isNativePlatform() && isRevenueCatWebRedemptionUrl(urlString)
  );
}

export function buildWebPurchaseRedemptionUrl(redemptionToken: string): string {
  const encoded = encodeURIComponent(redemptionToken);
  return `${REVENUECAT_WEB_REDEMPTION_URL_SCHEME}://redeem_web_purchase?redemption_token=${encoded}`;
}

export function openWebPurchaseRedemptionDialog(urlString: string): void {
  const existing = usePaymentsStore.getState().webPurchaseRedemptionSession;
  if (existing?.url === urlString) return;

  webPurchaseRedemptionSessionSeq += 1;
  usePaymentsStore.getState().setWebPurchaseRedemptionSession({
    sessionId: webPurchaseRedemptionSessionSeq,
    url: urlString,
    outcome: { kind: "idle" },
    isRedeeming: false,
  });
}

export function closeWebPurchaseRedemptionDialog(): void {
  usePaymentsStore.getState().setWebPurchaseRedemptionSession(null);
}

export function resetWebPurchaseRedemptionOutcome(): void {
  usePaymentsStore.getState().patchWebPurchaseRedemptionSession({
    outcome: { kind: "idle" },
    isRedeeming: false,
  });
}

function applyWebPurchaseRedemptionResult(
  result: WebPurchaseRedemptionResult | null
): void {
  const { patchWebPurchaseRedemptionSession } = usePaymentsStore.getState();

  if (!result) {
    patchWebPurchaseRedemptionSession({
      isRedeeming: false,
      outcome: { kind: "invalid" },
    });
    return;
  }

  switch (result.result) {
    case WebPurchaseRedemptionResultType.SUCCESS:
      void refreshEntitlementsAfterExternalGrant();
      patchWebPurchaseRedemptionSession({
        isRedeeming: false,
        outcome: { kind: "success" },
      });
      break;
    case WebPurchaseRedemptionResultType.ERROR:
      logException(result.error);
      patchWebPurchaseRedemptionSession({
        isRedeeming: false,
        outcome: {
          kind: "error",
          message:
            result.error.message ??
            "We couldn't link your purchase. Please try again.",
        },
      });
      break;
    case WebPurchaseRedemptionResultType.PURCHASE_BELONGS_TO_OTHER_USER:
      patchWebPurchaseRedemptionSession({
        isRedeeming: false,
        outcome: { kind: "wrong_user" },
      });
      break;
    case WebPurchaseRedemptionResultType.INVALID_TOKEN:
      patchWebPurchaseRedemptionSession({
        isRedeeming: false,
        outcome: { kind: "invalid" },
      });
      break;
    case WebPurchaseRedemptionResultType.EXPIRED:
      patchWebPurchaseRedemptionSession({
        isRedeeming: false,
        outcome: {
          kind: "expired",
          obfuscatedEmail: result.obfuscatedEmail,
        },
      });
      break;
  }
}

/** Attempts to redeem the URL stored in the active redemption dialog session. */
export async function redeemWebPurchaseInDialog(): Promise<void> {
  const session = usePaymentsStore.getState().webPurchaseRedemptionSession;
  if (!session?.url || session.isRedeeming) return;

  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return;

  usePaymentsStore.getState().patchWebPurchaseRedemptionSession({
    isRedeeming: true,
  });

  try {
    const result = await redeemWebPurchaseFromUrl(session.url);
    applyWebPurchaseRedemptionResult(result);
  } catch (e) {
    logException(e);
    console.log("Error redeeming web purchase", e);
    usePaymentsStore.getState().patchWebPurchaseRedemptionSession({
      isRedeeming: false,
      outcome: {
        kind: "error",
        message: "We couldn't link your purchase. Please try again.",
      },
    });
  }
}

/**
 * Handles RevenueCat web-billing redemption deep links (`rc-*://`).
 * Returns true when the URL is a redemption link (including when queued until RC is ready).
 */
export async function handleWebPurchaseRedemptionLink(
  urlString: string
): Promise<boolean> {
  if (!isWebPurchaseRedemptionLink(urlString)) {
    return false;
  }

  openWebPurchaseRedemptionDialog(urlString);

  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) {
    pendingWebPurchaseRedemptionUrl = urlString;
    return true;
  }

  pendingWebPurchaseRedemptionUrl = null;
  return true;
}

async function flushPendingWebPurchaseRedemption(): Promise<void> {
  const url = pendingWebPurchaseRedemptionUrl;
  if (!url) return;
  pendingWebPurchaseRedemptionUrl = null;
  openWebPurchaseRedemptionDialog(url);
}

/**
 * A native RC UI promise that never settles (e.g. a presentation that failed
 * without rejecting) would otherwise brick every RC surface — paywalls and
 * the manage-subscriptions gear — until app restart. If the user is still
 * interacting with the app this long after we "presented" a full-screen
 * modal, the modal isn't actually on screen; let a fresh attempt through.
 */
const RC_UI_STUCK_MS = 90_000;
let revenueCatUiInFlightSince = 0;

function runExclusiveRevenueCatUi<T>(fn: () => Promise<T>): Promise<T> {
  if (revenueCatUiInFlight) {
    if (Date.now() - revenueCatUiInFlightSince < RC_UI_STUCK_MS) {
      console.warn("[paywall] RevenueCat UI already in flight — returning existing presentation");
      return revenueCatUiInFlight as Promise<T>;
    }
    console.warn("[paywall] RevenueCat UI in-flight guard stuck — allowing a new attempt");
    logException(new Error("RevenueCat UI in-flight guard stuck > 90s; self-healing"));
  }
  usePaymentsStore.getState().setRevenueCatModalPresenting(true);
  revenueCatUiInFlightSince = Date.now();
  const p = Promise.resolve()
    .then(fn)
    .finally(() => {
      // A superseded (stuck) promise settling late must not clear the guard
      // of the attempt that replaced it.
      if (revenueCatUiInFlight === p) {
        revenueCatUiInFlight = null;
        usePaymentsStore.getState().setRevenueCatModalPresenting(false);
      }
    });
  revenueCatUiInFlight = p;
  return p as Promise<T>;
}

/**
 * Configures RevenueCat. On web, mount `PaywallHost` once (e.g. in `App`) so paywalls receive
 * a proper panel mount target.
 */
export function usePayments(): void {
  const { account } = useAuthStore();
  const [configuredAccountId, setConfiguredAccountId] = useState<string | null>(
    null
  );

  useEffect(() => {
    // Clear entitlement cache on account change
    cachedEntitlements = null;

    // disable revenuecat in the emulator to avoid poluting staging with missing customers
    if (!account) {
      usePaymentsStore.getState().setIsReady(false);
      return;
    }
    // First point in the app's life where RevenueCat is genuinely needed, so
    // this is where its chunk gets pulled down. An account always exists by
    // now, meaning no anonymous /explore visitor pays for the SDK.
    void loadRevenueCatPlatform().then((platform) =>
      platform.syncAccountInEffect(account, {
        configuredAccountId,
        setConfiguredAccountId,
        triggerSubscriptionRefresh: () => {
          void refreshActiveSubscriptions();
        },
        setRevenueCatAccountSynced: (synced) => {
          usePaymentsStore.getState().setIsReady(synced);
          if (synced) {
            void flushPendingWebPurchaseRedemption();
            // Prefetch the routine paywall experiment offering so the variant
            // assignment is cached before the user reaches auth-link / select-routine.
            void prefetchRoutinePaywallExperiment();
          }
        },
      })
    );
  }, [account?.ref?.id, configuredAccountId]);

  useEffect(() => {
    return () => {
      // Peek, not load: if RevenueCat was never initialised there is nothing
      // to dispose, and unmount must not trigger a download.
      peekRevenueCatPlatform()?.dispose();
    };
  }, []);
}

export { PaywallHost } from "@/components/PaywallHost";

const ENTITLEMENT_REFRESH_RETRY_MS = 1_000;

/** Clears the in-memory entitlement cache used by {@link getEntitlements}. */
export function clearEntitlementsCache(): void {
  cachedEntitlements = null;
}

/**
 * Resets web paywall shell / visibility state. Use when routing into flows that
 * grant access outside the normal purchase path (e.g. wholesaler redeem).
 */
export function dismissPaywallUi(): void {
  usePaymentsStore.getState().setIsPaywallVisible(false);
  usePaymentsStore.getState().setWebPaywallHostSession(null);
  usePaymentsStore.getState().setRevenueCatModalPresenting(false);
}

/**
 * Refreshes entitlements after a grant made outside the app (wholesaler redeem,
 * RC dashboard, etc.). Invalidates the RevenueCat SDK cache, refetches, and
 * retries once when expected entitlements are not yet visible.
 */
export async function refreshEntitlementsAfterExternalGrant(
  expectedEntitlements?: string[]
): Promise<{ entitlements: string[]; allExpectedPresent: boolean }> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) {
    return { entitlements: [], allExpectedPresent: false };
  }

  const fetchFresh = async (): Promise<string[]> => {
    clearEntitlementsCache();
    await platform.invalidateCustomerInfoCache();
    await refreshActiveSubscriptions();
    return getEntitlements();
  };

  let entitlements = await fetchFresh();
  let allExpectedPresent = hasExpectedEntitlements(
    entitlements,
    expectedEntitlements
  );

  if (!allExpectedPresent && expectedEntitlements?.length) {
    await new Promise((resolve) => setTimeout(resolve, ENTITLEMENT_REFRESH_RETRY_MS));
    entitlements = await fetchFresh();
    allExpectedPresent = hasExpectedEntitlements(
      entitlements,
      expectedEntitlements
    );
  }

  return { entitlements, allExpectedPresent };
}

function hasExpectedEntitlements(
  active: string[],
  expected?: string[]
): boolean {
  if (!expected?.length) {
    return true;
  }
  return expected.every((id) => active.includes(id));
}

/** Refreshes subscription summaries into {@link usePaymentsStore}. */
export async function refreshActiveSubscriptions(): Promise<void> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return;

  const {
    setSubscriptionsLoading,
    setSubscriptions,
    setSubscriptionsLastFetchedAt,
  } = usePaymentsStore.getState();

  setSubscriptionsLoading(true);
  try {
    const snapshots = await platform.fetchLatestSubscriptionSnapshot();
    setSubscriptions(snapshots);
    setSubscriptionsLastFetchedAt(Date.now());

    // Update the memory cache with the fresh entitlements
    const entitlements = await platform.getEntitlements();
    cachedEntitlements = entitlements;
  } catch (e) {
    console.error("Error fetching subscription details", e);
    logException(e);
    setSubscriptions([]);
    cachedEntitlements = null;
  } finally {
    setSubscriptionsLoading(false);
  }
}

/** One-off fetch without updating the store (e.g. after a purchase before global refresh). */
export async function getLatestSubscriptionSnapshot(): Promise<
  SubscriptionSnapshot[]
> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return [];
  try {
    return await platform.fetchLatestSubscriptionSnapshot();
  } catch (e) {
    console.error("Error fetching subscription details", e);
    logException(e);
    return [];
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Status of one promotional entitlement grant. */
export type PromotionStatus = {
  /** Total length of the grant in days (purchase → expiry). */
  daysTotal: number;
  /** Whole days left until expiry, rounded up; 0 once expired. */
  daysRemaining: number;
  isExpired: boolean;
  /** When the grant started (purchase date). */
  startedAt: Date;
  /** Exact expiry — for hour-level countdowns and "Ends …" labels. */
  expiresAt: Date;
};

/**
 * Promotional grants keyed by subscription entitlement
 * (`basic` / `clinical_support` — see `SUBSCRIPTION_ENTITLEMENTS`).
 * Entitlements without a promotional grant on record are absent.
 */
export type CurrentPromotions = Partial<
  Record<EntitlementIdentifier, PromotionStatus>
>;

/**
 * Returns the user's RevenueCat **promotional entitlements** (store
 * `PROMOTIONAL`) — e.g. the account-creation promo grant (`docs/paywall.md`
 * §7.5) or a redeemed wholesaler code (§11). Entitlements currently backed by a
 * store subscription are not reported here. Includes lapsed grants with
 * `isExpired: true`. Empty when RevenueCat is not ready or nothing promotional
 * is on record.
 */
export async function getCurrentPromotions(): Promise<CurrentPromotions> {
  const snapshots = await getLatestSubscriptionSnapshot();
  const promotions: CurrentPromotions = {};
  const nowMs = Date.now();

  for (const snapshot of snapshots) {
    if (snapshot.purchaseStore !== "PROMOTIONAL") continue;
    // Promotional grants always carry an expiry; skip defensively if missing.
    if (!snapshot.expiresDate) continue;

    const expiresMs = snapshot.expiresDate.getTime();
    const purchaseMs = snapshot.purchaseDate.getTime();
    promotions[snapshot.entitlementId] = {
      daysTotal: Math.max(0, Math.round((expiresMs - purchaseMs) / DAY_MS)),
      daysRemaining: Math.max(0, Math.ceil((expiresMs - nowMs) / DAY_MS)),
      isExpired: expiresMs <= nowMs,
      startedAt: snapshot.purchaseDate,
      expiresAt: snapshot.expiresDate,
    };
  }

  return promotions;
}

/** Presents the paywall so the user can subscribe or change plans. */
export async function presentSubscribePaywall(): Promise<boolean> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return Promise.resolve(false);
  return runExclusiveRevenueCatUi(() => platform.presentSubscribePaywall());
}

export async function presentPaywallIfNeeded(
  options: PaywallPresentationRequest
): Promise<boolean> {
  const account = useAuthStore.getState().account;
  if (account && account.attributes?.paywallClass !== "new") {
    console.log(
      "[paywall] Grandfathered account - presentPaywallIfNeeded bypass"
    );
    return Promise.resolve(true);
  }

  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return Promise.resolve(false);
  return runExclusiveRevenueCatUi(() => {
    if (options.placement) {
      console.log("[placement] 📍", options.placement);
      return platform.presentPaywallIfNeeded(options);
    }
    return Promise.resolve(false);
  });
}

/**
 * Returns true if the user already has the entitlement or gains it via the paywall.
 * When the platform is not ready yet, returns true so local/dev flows are not blocked.
 * Otherwise delegates to {@link presentPaywallIfNeeded}, which returns true for an
 * already-active entitlement on web (`isEntitledTo`). Native uses RevenueCatUI’s
 * `presentPaywallIfNeeded` (`NOT_PRESENTED` counts as access as it is returned when
 *  placement does not need to requre access); on `ERROR`, we still
 * succeed if `customerInfo` already shows the entitlement active, in case access is still given
 */
export async function ensureEntitlementOrPaywall(
  options: Omit<PaywallPresentationRequest, "onHardDismiss">
): Promise<boolean> {
  const { entitlement } = options;

  // 1. Grandfathered account check
  const account = useAuthStore.getState().account;
  if (account && account.attributes?.paywallClass !== "new") {
    console.log(
      "[paywall] Grandfathered account - bypassing entitlement check"
    );
    return true;
  }

  // 2. Cache-first check if platform is ready
  const platform = await loadRevenueCatPlatform();
  if (platform.isReady()) {
    try {
      const entitlements = await getEntitlements();
      if (entitlements.includes(entitlement)) {
        console.log("[paywall] Entitlement active via cache:", entitlement);
        // Refresh cache in the background so any lapse is caught quickly
        void refreshActiveSubscriptions();
        return true;
      }
    } catch (e) {
      console.error("[paywall] Error checking cached entitlements:", e);
    }
  }

  // 3. Fallback for emulator / local development when platform is not ready
  if (!platform.isReady()) {
    console.warn(
      "An entitment is required for an action but RevenueCat is not yet initialized. This is likely a bug."
    );
    return true;
  }

  const result = await presentPaywallIfNeeded({
    ...options,
    onHardDismiss: () => {
      console.log(
        "Aborting action becuse the user does not have the entitlement and the paywall was dismissed."
      );
    },
  });
  if (result) {
    cachedEntitlements = null;
    void refreshActiveSubscriptions();
  }
  return result;
}

/**
 * Cache of `${accountId}:${tag}` keys we've already stamped to the GA / Firestore /
 * RC sinks this session. When marketing concludes one experiment and activates a new
 * one, the resolved offering changes, the dedupe key misses, and the variant tag is
 * rewritten everywhere — see `docs/paywall.md` §7.3.
 */
const recordedExperimentTags = new Set<string>();

/**
 * Prefetches both routine-paywall placements (auth-link + routine-click) so RC
 * commits the variant assignment, then stamps `routinePaywallExperiment` onto the
 * GA user property, the Firestore Account doc, and the RC subscriber attribute.
 * Idempotent per `(account, tag)` for the React session.
 *
 * The tag format is `<placement-label>:<offering-id>` so the same offering can be
 * shared across variants while still being distinguishable in funnels — variant A
 * is tagged `auth-link:spark_default` and variant B is tagged
 * `routine-click:spark_default` even when both reference the same `spark_default`
 * offering. Marketing doesn't have to duplicate offerings just to differentiate
 * variants; the placement disambiguates.
 *
 * Why both placements? The experiment lives at `ROUTINE_PAYWALL_CLICK_PLACEMENT`.
 * RC's targeting at `ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT` excludes users assigned
 * to variant B of that experiment. So:
 * - Variant A users see no offering at click, see `spark_default` (or whatever
 *   the Pay-at-Login rule serves) at auth-link → tagged via auth-link.
 * - Variant B users see an offering at click, see nothing at auth-link → tagged
 *   via click.
 * Querying both early means the variant is locked in and the tag is reported
 * regardless of which step the user actually lands on first (e.g. resumed
 * onboarding mid-flow).
 *
 * Called from `usePayments` once RC is ready — see `docs/paywall.md` §7.4.
 */
export async function prefetchRoutinePaywallExperiment(): Promise<void> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return;

  const account = useAuthStore.getState().account;
  if (!account?.ref) return;

  try {
    // Order matters: query the experiment placement first so RC commits the
    // variant assignment before the auth-link audience filter is evaluated.
    const clickOffering = await platform.prefetchExperimentOffering(
      ROUTINE_PAYWALL_CLICK_PLACEMENT
    );
    const authLinkOffering = await platform.prefetchExperimentOffering(
      ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT
    );

    // Short labels for GA/Firestore — not RevenueCat dashboard identifiers. RC uses the
    // full placement strings (e.g. ROUTINE_PAYWALL_CLICK_PLACEMENT ends in …/click).
    let placementLabel: "auth-link" | "routine-click";
    let offeringId: string;
    if (clickOffering) {
      placementLabel = "routine-click";
      offeringId = clickOffering.identifier;
    } else if (authLinkOffering) {
      placementLabel = "auth-link";
      offeringId = authLinkOffering.identifier;
    } else {
      console.log("[payments] no routine paywall experiment offering");
      return;
    }

    const tag = `${placementLabel}:${offeringId}`;

    console.log(
      "[payments] prefetched experiment offering",
      `tag=${tag} (via ${placementLabel})`
    );

    const dedupeKey = `${account.ref.id}:${tag}`;
    if (recordedExperimentTags.has(dedupeKey)) return;
    recordedExperimentTags.add(dedupeKey);

    logUserProperty("routinePaywallExperiment", tag);

    await Promise.all([
      useAuthStore
        .getState()
        .updateAccountSettings({ routinePaywallExperiment: tag })
        .catch((e) => {
          console.error(
            "Error writing routinePaywallExperiment to Settings",
            e
          );
          logException(e);
        }),
      platform.setSubscriberAttributes({
        routinePaywallExperiment: tag,
      }),
    ]);
  } catch (e) {
    console.error("Error prefetching routine paywall experiment", e);
    logException(e);
  }
}

/**
 * After RevenueCat session sync ({@link usePaymentsStore}.`isReady`), ensures the entitlement
 * or presents the paywall. Hard dismiss navigates back.
 * Pass `condition: false` to skip (e.g. same page handles both new and existing characters).
 */
export function usePageEntitlementPaywall(
  options: Pick<PaywallPresentationRequest, "entitlement" | "placement"> & {
    condition?: boolean;
    noNavigationOnDismiss?: boolean;
  }
): void {
  const isReady = usePaymentsStore((s) => s.isReady);
  const navigate = useNavigate();
  const {
    entitlement,
    placement,
    condition = true,
    noNavigationOnDismiss,
  } = options;

  useEffect(() => {
    if (!condition || !isReady) return;
    void (async () => {
      await presentPaywallIfNeeded({
        entitlement,
        placement,
        onHardDismiss: noNavigationOnDismiss
          ? undefined
          : () => {
              navigate(-1);
            },
      });
    })();
  }, [
    condition,
    isReady,
    entitlement,
    placement,
    navigate,
    noNavigationOnDismiss,
  ]);
}

export async function manageSubscriptions(): Promise<void> {
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) {
    // Loud instead of a silent no-op: the single caller (ProfileMenu) surfaces
    // this and falls back to the OS subscription-management page.
    return Promise.reject(new Error("RevenueCat is not ready — cannot open subscription management"));
  }
  return runExclusiveRevenueCatUi(() => platform.manageSubscriptions());
}

export async function getEntitlements(): Promise<string[]> {
  if (cachedEntitlements !== null) {
    return cachedEntitlements;
  }
  const platform = await loadRevenueCatPlatform();
  if (!platform.isReady()) return [];
  try {
    const entitlements = await platform.getEntitlements();
    cachedEntitlements = entitlements;
    return entitlements;
  } catch (e) {
    console.error("Error fetching entitlements", e);
    return [];
  }
}
